import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    TextField,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collectLotKeys, sessionBelongsToLots } from '../sessionScope';

interface Session {
    id: string;
    sessionID: string;
    userEmail: string;
    lotName: string;
    lotID?: string;
    // Kept alongside the formatted strings so filtering and sorting work on
    // a real Date instead of a localised rendering of one.
    startedAt: Date | null;
    vehicle?: {
        licensePlate: string;
        make: string;
        color: string;
    };
    startDate: string;
    startTime: string;
    paymentAmount?: number;
    paymentMethod?: string;
    sessionActive: boolean;
}

/** Local calendar date as YYYY-MM-DD, matching what <input type="date"> emits. */
function toIsoDate(d: Date): string {
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}

interface VendorSessionHistoryProps {
    vendorId: string;
}

export default function VendorSessionHistory({ vendorId }: VendorSessionHistoryProps) {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);

    // Search filters
    const [searchLicense, setSearchLicense] = useState('');
    const [searchDate, setSearchDate] = useState('');
    const [searchEmail, setSearchEmail] = useState('');

    const fetchVendorLots = async () => {
        try {
            const lotsQuery = query(
                collection(db, 'parkinglots'),
                where('vendorId', '==', vendorId)
            );
            const lotsSnapshot = await getDocs(lotsQuery);
            return collectLotKeys(lotsSnapshot.docs);
        } catch (error) {
            console.error('Error fetching vendor lots:', error);
            return { lotIds: new Set<string>(), lotNames: new Set<string>() };
        }
    };

    const fetchClosedSessions = async () => {
        setLoading(true);
        try {
            const { lotIds, lotNames } = await fetchVendorLots();

            if (lotIds.size === 0) {
                setSessions([]);
                setFilteredSessions([]);
                setLoading(false);
                return;
            }

            // Get all closed sessions
            const sessionsQuery = query(
                collection(db, 'parkingsessions'),
                where('sessionActive', '==', false)
            );
            const sessionsSnapshot = await getDocs(sessionsQuery);

            // Filter for sessions at vendor's lots
            const vendorSessions: Session[] = sessionsSnapshot.docs
                .map(doc => {
                    const data = doc.data();

                    const startTs = data.startTime;
                    const startDateObj =
                        startTs && typeof startTs.toDate === 'function'
                            ? startTs.toDate()
                            : null;

                    return {
                        id: doc.id,
                        sessionID: doc.id,

                        userEmail: data.customerInfo?.email ?? '',
                        lotName: data.lotName ?? '',
                        lotID: data.lotID ?? undefined,
                        startedAt: startDateObj,

                        vehicle: data.vehicleInfo
                            ? {
                                licensePlate: data.vehicleInfo.licensePlate ?? '',
                                make: data.vehicleInfo.make ?? '',
                                color: data.vehicleInfo.color ?? ''
                            }
                            : undefined,

                        startDate: startDateObj
                            ? startDateObj.toLocaleDateString()
                            : '',

                        startTime: startDateObj
                            ? startDateObj.toLocaleTimeString()
                            : '',

                        paymentAmount: typeof data.amount === 'number'
                            ? data.amount
                            : undefined,

                        paymentMethod: data.gateway ?? undefined,

                        sessionActive: Boolean(data.sessionActive)
                    };
                })
                .filter(session => sessionBelongsToLots(session, lotIds, lotNames))
                .sort((a, b) =>
                    (b.startedAt?.getTime() ?? 0) - (a.startedAt?.getTime() ?? 0)
                );


            setSessions(vendorSessions);
            setFilteredSessions(vendorSessions);
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClosedSessions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const handleSearch = () => {
        let filtered = [...sessions];

        // Filter by license plate
        if (searchLicense.trim()) {
            filtered = filtered.filter(session =>
                session.vehicle?.licensePlate?.toUpperCase().includes(searchLicense.toUpperCase())
            );
        }

        // Filter by date. The picker gives YYYY-MM-DD; session.startDate is a
        // locale-formatted string (M/D/YYYY in en-US), so comparing the two
        // directly never matched. Compare against the underlying Date instead.
        if (searchDate.trim()) {
            filtered = filtered.filter(session =>
                session.startedAt !== null &&
                toIsoDate(session.startedAt) === searchDate
            );
        }

        // Filter by email
        if (searchEmail.trim()) {
            filtered = filtered.filter(session =>
                session.userEmail?.toLowerCase().includes(searchEmail.toLowerCase())
            );
        }

        setFilteredSessions(filtered);
    };

    const handleClearSearch = () => {
        setSearchLicense('');
        setSearchDate('');
        setSearchEmail('');
        setFilteredSessions(sessions);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1,
                mb: { xs: 1.5, sm: 3 }
            }}>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    Session History
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchClosedSessions}
                    size="small"
                >
                    Refresh
                </Button>
            </Box>

            {/* Search Filters - more compact on mobile */}
            <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: { xs: 1.5, sm: 3 }, overflow: 'hidden' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' }, mb: { xs: 1, sm: 2 } }}>
                    Search Filters
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                    gap: { xs: 1, sm: 2 },
                    maxWidth: '100%'
                }}>
                    <TextField
                        label="Plate"
                        value={searchLicense}
                        onChange={(e) => setSearchLicense(e.target.value)}
                        placeholder="ABC1234"
                        size="small"
                        sx={{ minWidth: 0 }}
                    />
                    <TextField
                        label="Date"
                        type="date"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 0 }}
                    />
                    <TextField
                        label="Email"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        placeholder="user@example.com"
                        size="small"
                        sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}
                    />
                </Box>
                <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        size="small"
                    >
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClearSearch}
                        size="small"
                    >
                        Clear
                    </Button>
                </Box>
            </Paper>

            {/* Results */}
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Showing {filteredSessions.length} of {sessions.length} sessions
            </Typography>

            {filteredSessions.length === 0 ? (
                <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No closed sessions found
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Session ID</TableCell>
                                    <TableCell>Plate</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Vehicle</TableCell>
                                    <TableCell>Lot</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Time</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Customer</TableCell>
                                    <TableCell>Paid</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSessions.map((session) => (
                                    <TableRow key={session.id}>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.75rem' }}>{session.sessionID}</TableCell>
                                        <TableCell>
                                            <strong style={{ fontSize: '0.85rem' }}>{session.vehicle?.licensePlate || 'N/A'}</strong>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.8rem' }}>
                                            {session.vehicle?.make || 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{session.lotName}</TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{session.startDate}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.8rem' }}>{session.startTime}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.75rem' }}>{session.userEmail}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem' }}>
                                            ${(session.paymentAmount || 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
}